// ---------------- EmailEnviarPro.jsx -----------------

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  IconButton,
  Collapse,
  Menu,
  MenuItem,
  Select,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import axios from "axios";
import TipTapEditorNew from "./TipTapEditorNew";
import DropZoneWrapper from "../dropwrapper/DropZoneWrapper";

const API = process.env.REACT_APP_API_URL;

const ACCENT = "#00bbcd";
const ACCENT_HOVER = "#00a5b5";
const ON_ACCENT = "#0f172a";

const accentBtnSx = {
  bgcolor: ACCENT,
  color: ON_ACCENT,
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
};

const accentOutlinedSx = {
  color: ACCENT,
  borderColor: ACCENT,
  "&:hover": {
    borderColor: ACCENT_HOVER,
    bgcolor: "rgba(0, 187, 205, 0.08)",
  },
};

const accentTextSx = {
  color: ACCENT,
  "&:hover": { bgcolor: "rgba(0, 187, 205, 0.08)" },
};

export default function EmailEnviarPro({ open, onClose, onSent, accounts }) {
  const [fromEmail, setFromEmail] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const [selectedSignature, setSelectedSignature] = useState("main");
  const [signatures, setSignatures] = useState({ main: "", reply: "" });

  const [previewOpen, setPreviewOpen] = useState(false);

  /* ------------------------------------------------------
      RESET FORM — solo al cerrar (NO al abrir)
  ------------------------------------------------------ */
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    // ❌ NO tocamos fromEmail
    setTo("");
    setCc("");
    setBcc("");
    setSubject("");
    setAttachments([]);
    setSending(false);
    setShowCc(false);
    setShowBcc(false);
    setSelectedSignature("main");
    setHtml("");
  };

  /* ------------------------------------------------------
      GENERAR LA VISTA PREVAIA DE LA FIRMA
  ------------------------------------------------------ */

  const getSignaturePreviewHTML = () => {
    const sig =
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "";

    let fixedSig = sig;

    if (signatures.logo_path) {
      fixedSig = fixedSig.replace(
        /cid:signature_logo/g,
        `${API}${signatures.logo_path}`
      );
    }

    return fixedSig;
  };

  /* ------------------------------------------------------
      CARGAR CUENTA POR DEFECTO CUANDO ABRE
  ------------------------------------------------------ */
  useEffect(() => {
    if (open && accounts?.length > 0) {
      setFromEmail(accounts[0].email.toLowerCase());
    }
  }, [open, accounts]);

  /* ------------------------------------------------------
      CARGAR FIRMAS AL CAMBIAR CUENTA O TIPO DE FIRMA
  ------------------------------------------------------ */
  useEffect(() => {
    if (!open) return;
    if (!fromEmail) return;

    const loadSignatures = async () => {
      try {
        const res = await axios.get(`${API}/signatures/${fromEmail}`);

        const main = res.data.signature_html || "";
        const reply = res.data.reply_signature_html || main;
        setSignatures({
          main,
          reply,
          logo_path: res.data.logo_path || null,
        });

        // NO insertar firma en el editor
        setHtml("");
      } catch (err) {
        console.warn("⚠️ No hay firmas para esta cuenta:", fromEmail);
        setSignatures({ main: "", reply: "", logo_path: null });

        setHtml("");
      }
    };

    loadSignatures();
  }, [open, fromEmail, selectedSignature]);

  /* ------------------------------------------------------
      FILE HANDLING
  ------------------------------------------------------ */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (name) => {
    setAttachments((prev) => prev.filter((f) => f.name !== name));
  };

  /* ------------------------------------------------------
      TEMPLATES
  ------------------------------------------------------ */
  const templates = {
    followUp: `
      Hola,<br><br>
      Te escribo para hacer seguimiento al email anterior. 
      ¿Has tenido oportunidad de revisarlo?<br><br>
      Quedo atento,<br>
    `,
    invoice: `
      Hola,<br><br>
      Te adjunto la factura correspondiente.<br>
      Si necesitas cualquier aclaración, estoy disponible.<br><br>
      Saludos,<br>
    `,
    welcome: `
      Hola,<br><br>
      ¡Bienvenido! Gracias por ponerte en contacto con nosotros.<br>
      ¿En qué podemos ayudarte?<br><br>
      Un saludo,<br>
    `,
  };

  const applyTemplate = (templateHTML) => {
    const sig =
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "";

    setHtml(templateHTML + sig);
    setAnchorEl(null);
  };

  /* ------------------------------------------------------
      SEND EMAIL
  ------------------------------------------------------ */
  const sendEmail = async () => {
    if (!fromEmail) {
      alert("Debe seleccionar una cuenta para enviar.");
      return;
    }

    if (!to.trim()) {
      alert("Debe haber al menos un destinatario.");
      return;
    }

    if (!subject.trim()) {
      alert("El asunto no puede estar vacío.");
      return;
    }

    try {
      setSending(true);

      const normalize = (str) => str.replace(/;/g, ",").replace(/\s+/g, "");

      const form = new FormData();
      form.append("fromEmail", fromEmail);
      form.append("to", normalize(to));
      form.append("cc", normalize(cc));
      form.append("bcc", normalize(bcc));
      form.append("subject", subject);
      form.append("html", html);
      form.append("isReply", "false"); // ✅ añadir esta línea

      attachments.forEach((file) => form.append("attachments", file));

      const res = await axios.post(`${API}/emails/send`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (onSent) onSent(res.data);
      onClose();
    } catch (err) {
      console.error(err);
      alert("❌ Error enviando email");
    } finally {
      setSending(false);
    }
  };

  /* ------------------------------------------------------
      UI
  ------------------------------------------------------ */
  return (
    <>
      <Dialog
        open={open}
        onClose={sending ? null : onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <span>Nuevo mensaje</span>
          <IconButton onClick={onClose} disabled={sending}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          {/* FROM */}
          <Select
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
          >
            {accounts.map((acc) => (
              <MenuItem key={acc.id} value={acc.email.toLowerCase()}>
                {acc.email}
              </MenuItem>
            ))}
          </Select>

          {/* PARA */}
          <TextField
            fullWidth
            label="Para"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            sx={{ mb: 1 }}
          />

          {/* SELECT FIRMA */}
          <Select
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            value={selectedSignature}
            onChange={(e) => setSelectedSignature(e.target.value)}
          >
            <MenuItem value="main">Firma principal</MenuItem>
            <MenuItem value="reply">Firma de respuesta</MenuItem>
            <MenuItem value="none">Sin firma</MenuItem>
          </Select>

          {/* CC / BCC */}
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Button
              size="small"
              onClick={() => setShowCc((x) => !x)}
              endIcon={<KeyboardArrowDownIcon />}
              sx={accentTextSx}
            >
              CC
            </Button>
            <Button
              size="small"
              onClick={() => setShowBcc((x) => !x)}
              endIcon={<KeyboardArrowDownIcon />}
              sx={accentTextSx}
            >
              BCC
            </Button>

            {/* TEMPLATES */}
            <Box sx={{ ml: "auto" }}>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem onClick={() => applyTemplate(templates.followUp)}>
                  Seguimiento
                </MenuItem>
                <MenuItem onClick={() => applyTemplate(templates.invoice)}>
                  Enviar Factura
                </MenuItem>
                <MenuItem onClick={() => applyTemplate(templates.welcome)}>
                  Bienvenida
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          <Collapse in={showCc}>
            <TextField
              fullWidth
              label="CC"
              size="small"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              sx={{ mb: 1 }}
            />
          </Collapse>

          <Collapse in={showBcc}>
            <TextField
              fullWidth
              label="BCC"
              size="small"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              sx={{ mb: 1 }}
            />
          </Collapse>

          {/* SUBJECT */}
          <TextField
            fullWidth
            label="Asunto"
            size="small"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* ATTACHMENTS */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            component="label"
            sx={{ mb: 1, ...accentOutlinedSx }}
          >
            Adjuntar archivos
            <input type="file" hidden multiple onChange={handleFileChange} />
          </Button>

          {attachments.length > 0 && (
            <Box
              sx={{ mt: 1, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}
            >
              {attachments.map((file) => (
                <Chip
                  key={file.name}
                  label={file.name}
                  onDelete={() => removeAttachment(file.name)}
                  sx={{ fontSize: 12 }}
                />
              ))}
            </Box>
          )}

          {/* EDITOR */}
          <DropZoneWrapper
            onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
          >
            <TipTapEditorNew
              key={selectedSignature + fromEmail}
              value={html}
              onChange={setHtml}
            />
          </DropZoneWrapper>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            onClick={() => setPreviewOpen(true)}
            disabled={sending}
            sx={accentOutlinedSx}
          >
            Vista previa firma
          </Button>

          <Box>
            <Button onClick={onClose} disabled={sending} sx={accentTextSx}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={sendEmail}
              disabled={sending}
              sx={{ ml: 2, ...accentBtnSx }}
            >
              {sending ? "Enviando..." : "Enviar"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/*  VISUALIZACION EMAIL */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Vista previa de la firma</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              border: "1px solid #ccc",
              p: 2,
              background: "#fff",
              minHeight: 150,
            }}
            dangerouslySetInnerHTML={{ __html: getSignaturePreviewHTML() }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
